<template>
    <MainVariantTileWrapper ref="mainVariantTileRef" :index="variantIndex" :link-target="variantLinkTarget">
        <template #main-variant-status>
            <Badge size="Small" :variant="variantStatusBadgeVariant">
                {{ variantStatusBadgeLabel }}
            </Badge>
        </template>

        <template #tiles>
            <div class="flex flex-row justify-end gap-1 p-1">
                <CardTile :class="phaseTileStyle">
                    <template #header>
                        <div v-if="phaseTileStatus === 'loaded'" class="flex flex-row items-center gap-1">
                            <PhasesIcon class="size-4" /> {{ $t('phases-tile-title') }}
                            <TextLink :to="linkToReferenceAllocation" class="font-light whitespace-nowrap">
                                {{ $t('phases-tile-title-reference-allocation') }}
                            </TextLink>
                            <span class="font-light whitespace-nowrap">
                                {{ $t('reference-allocation-duration-days', { durationInDays }) }}
                            </span>
                        </div>
                    </template>
                    <template #content>
                        <template v-if="phaseTileStatus === 'loaded'">
                            <PhasesTile>
                                <PhasesTilePhase
                                    v-for="phase in phaseTilePhases"
                                    v-bind="phase"
                                    :key="phase.labelShort"
                                />
                            </PhasesTile>
                        </template>
                        <p
                            v-else-if="phaseTileStatus === 'no-data'"
                            class="flex flex-row items-center justify-center gap-2 p-2 text-gray-500 w-full h-full text-sm"
                        >
                            <WarningIcon class="size-4" /> {{ $t('no-reference-allocation') }}
                        </p>
                        <p
                            v-else-if="phaseTileStatus === 'loading'"
                            class="flex flex-row items-center justify-center gap-2 p-2 text-gray-500 w-full h-full text-sm"
                        >
                            <PendingIcon class="size-4 animate-spin" /> {{ $t('loading-tile-data') }}
                        </p>
                    </template>
                </CardTile>

                <CardTile :class="locationsTileStyle">
                    <template #header>
                        <div v-if="showLocationsTile" class="flex flex-row items-center gap-1">
                            <LocationOnIcon class="size-4" /> {{ $t('federal-states') }}
                        </div>
                    </template>
                    <template #content>
                        <p
                            v-if="locationDotsLoading && showLocationsTile"
                            class="flex flex-row items-center justify-center gap-2 p-2 text-gray-500 w-full h-full text-sm"
                        >
                            <PendingIcon class="size-4 animate-spin" /> {{ $t('loading-tile-data') }}
                        </p>
                        <p
                            v-else-if="!locationDotsLoading && !showLocationsTile"
                            class="flex flex-row items-center justify-center gap-2 p-2 text-gray-500 w-full h-full text-sm"
                        >
                            <WarningIcon class="size-4" /> {{ $t('no-national-event') }}
                        </p>
                        <FederalStatesDotsTile v-else :sum="allocatedLocationsCount">
                            <FederalStatesDotsRow v-for="(mappedLocations, index) in locationChunks" :key="index">
                                <FederalStatesDot
                                    v-for="location in mappedLocations"
                                    :key="location.id"
                                    :state-name="location.name"
                                    :status="location.status"
                                    :default-tooltip-placement="location.defaultTooltipPlacement"
                                    :color="getCategoryColor(series?.category)"
                                />
                            </FederalStatesDotsRow>
                        </FederalStatesDotsTile>
                    </template>
                </CardTile>
            </div>
        </template>
    </MainVariantTileWrapper>
</template>

<script setup lang="ts">
import Badge, { BadgeVariant } from '@/components/Badge/Badge.vue';
import CardTile from '@/components/CardTile/CardTile.vue';
import FederalStatesDot from '@/components/FederalStatesDotsTile/FederalStatesDot.vue';
import FederalStatesDotsRow from '@/components/FederalStatesDotsTile/FederalStatesDotsRow.vue';
import FederalStatesDotsTile from '@/components/FederalStatesDotsTile/FederalStatesDotsTile.vue';
import LocationOnIcon from '@/components/Icon/LocationOnIcon.vue';
import PendingIcon from '@/components/Icon/PendingIcon.vue';
import PhasesIcon from '@/components/Icon/PhasesIcon.vue';
import WarningIcon from '@/components/Icon/WarningIcon.vue';
import MainVariantTileWrapper from '@/components/MainVariantTileWrapper/MainVariantTileWrapper.vue';
import { usePhaseTileData } from '@/components/PhasesTile/composables/usePhaseTileData';
import PhasesTile from '@/components/PhasesTile/PhasesTile.vue';
import PhasesTilePhase from '@/components/PhasesTile/PhasesTilePhase.vue';
import TextLink from '@/components/TextLink/TextLink.vue';
import { AllocationUsage } from '@/components/Timeline/allocation/types';
import { ExternalEventAllocationPhaseTileDataDocument } from '@/generated/graphql';
import { computed } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import { useExternalEventLocationDotsData } from '../composables/useExternalEventLocationDotsData';
import { useExternalEventSeriesInjection } from '../composables/useExternalEventSeriesInjection';
import { getCategoryColor } from '../externalEvent';

const props = defineProps<{
    eventId: string;
    variantId: string;
    variantIndex: number;
    isNational: boolean;
    variantStatusBadgeLabel: string;
    variantStatusBadgeVariant: BadgeVariant;
    variantLinkTarget: RouteLocationRaw;
    phaseTypes: AllocationUsage[];
    referenceAllocationId?: string;
    linkToReferenceAllocation: RouteLocationRaw;
}>();

const variantId = computed(() => props.variantId);
const referenceAllocationId = computed(() => props.referenceAllocationId);
const isNational = computed(() => props.isNational);

const phaseTileStyle = 'min-w-[289px]';
const locationsTileStyle = 'min-w-[242px]';

const { series } = useExternalEventSeriesInjection();

const eventId = computed(() => props.eventId);

const { durationInDays, phaseTilePhases, phaseTileStatus } = usePhaseTileData(
    ExternalEventAllocationPhaseTileDataDocument,
    props.phaseTypes,
    referenceAllocationId,
);

const {
    loading: locationDotsLoading,
    locationChunks,
    allocatedLocationsCount,
    showLocationsTile,
} = useExternalEventLocationDotsData(eventId, variantId, isNational);
</script>
